import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Select } from "../components/ui/select";
import { toast } from "sonner";
import { Eye, EyeOff } from "lucide-react";
import { registerUser } from "../services/crmServices/userService";
import { fetchPositions } from "../services/crmServices/positionService";

export function Register() {
    const navigate = useNavigate();
    const [formData, setFormData] = useState({
        firstName: "",
        lastName: "",
        userName: "",
        email: "",
        password: "",
        confirmPassword: "",
        phone: "",
        position: ""
    });
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [positions, setPositions] = useState([]);
    const [loadingPositions, setLoadingPositions] = useState(true);

    useEffect(() => {
        loadPositions();
    }, []);

    const loadPositions = async () => {
        try {
            setLoadingPositions(true);
            const response = await fetchPositions();
            if (response?.data) {
                setPositions(response.data);
                // Auto-select first position if available
                if (response.data.length > 0 && !formData.position) {
                    setFormData(prev => ({ ...prev, position: response.data[0]._id }));
                }
            }
        } catch (error) {
            console.error("Error loading positions:", error);
            // Don't show error - positions might require auth, we'll handle it in submit
            // Set positions to empty array and continue with registration
            setPositions([]);
            // Don't throw - allow registration to proceed without positions
        } finally {
            setLoadingPositions(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        // Validation
        if (!formData.firstName || !formData.lastName || !formData.userName || !formData.email || !formData.password) {
            toast.error("Please fill in all required fields");
            return;
        }

        if (formData.password !== formData.confirmPassword) {
            toast.error("Passwords do not match");
            return;
        }

        if (formData.password.length < 6) {
            toast.error("Password must be at least 6 characters long");
            return;
        }

        // Position is required but if not available, we'll let backend handle it
        // or admin can assign later

        // Email validation
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(formData.email)) {
            toast.error("Please enter a valid email address");
            return;
        }

        setIsLoading(true);

        try {
            const userData = {
                firstName: formData.firstName.trim(),
                lastName: formData.lastName.trim(),
                userName: formData.userName.trim(),
                email: formData.email.trim().toLowerCase(),
                password: formData.password,
                phone: formData.phone?.trim() || "",
                ...(formData.position && { position: formData.position }),
                active: true,
                isActive: true,
                is_active: true,
                viewLeads: "all",
                viewLeadsScope: "all",
                viewLeadsAllowedDesks: [],
                canFastLogin: false,
                permissions: {
                    leads: {
                        view: true,
                        create: true,
                        update: true,
                        delete: false,
                        viewEmail: true,
                        viewPhone: true,
                        viewActionHistory: true
                    },
                    staff: {
                        view: false,
                        create: false,
                        update: false,
                        delete: false
                    },
                    desks: {
                        view: false,
                        create: false,
                        update: false,
                        delete: false
                    },
                    gridLeadsColumns: {
                        source: true,
                        affiliate: true,
                        manager: true,
                        registered: true,
                        modified: true,
                        desk: true,
                        lastComment: true,
                        lastCommDate: true,
                        lastCommManager: true
                    },
                    editLeadFields: {
                        firstName: true,
                        lastName: true,
                        userName: true,
                        email: true,
                        phone: true,
                        secondPhone: true,
                        status: true,
                        country: true,
                        city: true,
                        address: true,
                        source: true,
                        extra: true
                    },
                    voip: {
                        commpeak: false,
                        voiso: false,
                        coperato: false,
                        squaretalk: false
                    }
                }
            };

            const response = await registerUser(userData);

            // Backend returns: { status: 200, data: { RESULT: "SUCCESS", message: "...", user: user } }
            // Or: { status: 400, data: { RESULT: "ERROR", message: "..." } }
            if (response?.status === 200 && response?.data?.RESULT === "SUCCESS") {
                toast.success(response?.data?.message || "Registration successful! Please login.");
                setTimeout(() => {
                    navigate("/login");
                }, 1500);
            } else if (response?.status === 400 && response?.data?.RESULT === "ERROR") {
                toast.error(response?.data?.message || "Registration failed. Please try again.");
            } else if (response?.data?.RESULT === "ERROR") {
                toast.error(response?.data?.message || "Registration failed. Please try again.");
            } else if (response?.data?.message) {
                // Handle other error formats
                toast.error(response.data.message);
            } else {
                console.error("Unexpected response format:", response);
                toast.error("Registration failed. Please try again.");
            }
        } catch (error) {
            console.error("Registration error:", error);
            console.error("Error response:", error?.response);
            
            // Handle axios error response
            if (error?.response?.data) {
                const errorData = error.response.data;
                if (errorData?.data?.RESULT === "ERROR") {
                    toast.error(errorData.data.message || "Registration failed.");
                } else if (errorData?.message) {
                    toast.error(errorData.message);
                } else {
                    toast.error("Registration failed. Please try again.");
                }
            } else {
                toast.error(error?.message || "Registration failed. Please try again.");
            }
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
            <div className="max-w-md w-full space-y-8">
                <div className="text-center">
                    <h2 className="mt-6 text-3xl font-extrabold text-gray-900">Create your account</h2>
                    <p className="mt-2 text-sm text-gray-600">Sign up to access the CRM system</p>
                </div>

                <Card>
                    <CardHeader>
                        <CardTitle>Register</CardTitle>
                        <CardDescription>Fill in your information to create an account</CardDescription>
                    </CardHeader>
                    <CardContent>
                        <form onSubmit={handleSubmit} className="space-y-4">
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <Label htmlFor="firstName">First Name *</Label>
                                    <Input
                                        id="firstName"
                                        type="text"
                                        value={formData.firstName}
                                        onChange={(e) => setFormData((prev) => ({ ...prev, firstName: e.target.value }))}
                                        placeholder="First name"
                                        required
                                    />
                                </div>
                                <div>
                                    <Label htmlFor="lastName">Last Name *</Label>
                                    <Input
                                        id="lastName"
                                        type="text"
                                        value={formData.lastName}
                                        onChange={(e) => setFormData((prev) => ({ ...prev, lastName: e.target.value }))}
                                        placeholder="Last name"
                                        required
                                    />
                                </div>
                            </div>

                            <div>
                                <Label htmlFor="userName">Username *</Label>
                                <Input
                                    id="userName"
                                    type="text"
                                    value={formData.userName}
                                    onChange={(e) => setFormData((prev) => ({ ...prev, userName: e.target.value }))}
                                    placeholder="Choose a username"
                                    required
                                />
                            </div>

                            <div>
                                <Label htmlFor="email">Email Address *</Label>
                                <Input
                                    id="email"
                                    type="email"
                                    value={formData.email}
                                    onChange={(e) => setFormData((prev) => ({ ...prev, email: e.target.value }))}
                                    placeholder="Enter your email"
                                    required
                                />
                            </div>

                            <div>
                                <Label htmlFor="phone">Phone Number</Label>
                                <Input
                                    id="phone"
                                    type="tel"
                                    value={formData.phone}
                                    onChange={(e) => setFormData((prev) => ({ ...prev, phone: e.target.value }))}
                                    placeholder="Enter your phone number"
                                />
                            </div>

                            <div>
                                <Label htmlFor="position">Position {positions.length > 0 && "*"}</Label>
                                {positions.length === 0 && !loadingPositions ? (
                                    <div className="text-sm text-gray-500 mb-2">
                                        Positions not available. An admin will assign your position after registration.
                                    </div>
                                ) : (
                                    <Select
                                        options={positions.map((position) => ({
                                            label: position.name || position.role,
                                            value: position._id
                                        }))}
                                        value={formData.position}
                                        onChange={(value) => setFormData((prev) => ({ ...prev, position: value }))}
                                        placeholder={loadingPositions ? "Loading..." : "Select a position"}
                                        disabled={loadingPositions}
                                        searchable
                                    />
                                )}
                            </div>

                            <div>
                                <Label htmlFor="password">Password *</Label>
                                <div className="relative">
                                    <Input
                                        id="password"
                                        type={showPassword ? "text" : "password"}
                                        value={formData.password}
                                        onChange={(e) => setFormData((prev) => ({ ...prev, password: e.target.value }))}
                                        placeholder="Enter your password"
                                        required
                                    />
                                    <Button
                                        type="button"
                                        variant="ghost"
                                        size="sm"
                                        className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                                        onClick={() => setShowPassword(!showPassword)}
                                    >
                                        {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                                    </Button>
                                </div>
                            </div>

                            <div>
                                <Label htmlFor="confirmPassword">Confirm Password *</Label>
                                <div className="relative">
                                    <Input
                                        id="confirmPassword"
                                        type={showConfirmPassword ? "text" : "password"}
                                        value={formData.confirmPassword}
                                        onChange={(e) => setFormData((prev) => ({ ...prev, confirmPassword: e.target.value }))}
                                        placeholder="Confirm your password"
                                        required
                                    />
                                    <Button
                                        type="button"
                                        variant="ghost"
                                        size="sm"
                                        className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                    >
                                        {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                                    </Button>
                                </div>
                            </div>

                            <Button type="submit" className="w-full" disabled={isLoading || loadingPositions}>
                                {isLoading ? "Creating account..." : "Register"}
                            </Button>

                            <div className="text-center text-sm">
                                <span className="text-gray-600">Already have an account? </span>
                                <Link to="/login" className="text-blue-600 hover:text-blue-700 font-medium">
                                    Sign in
                                </Link>
                            </div>
                        </form>
                    </CardContent>
                </Card>
            </div>
        </div>
    );
}

